import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../apiClient';
import Liveness from '../Components/Liveness';
import { validateIdCardImage } from '../utils/imageGuardrails';
import {
    PassportIllustration,
    IdCardIllustration,
    SelfieIllustration
} from '../Components/Illustrations';

const STAGES = {
    INTRO: 'INTRO',
    UPLOAD_ID: 'UPLOAD_ID',
    SELFIE_PREP: 'SELFIE_PREP',
    LIVENESS_STREAM: 'LIVENESS_STREAM',
    COMPLETED: 'COMPLETED',
    LOCKED: 'LOCKED'
};

const VerificationWizard = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const fileUploadInputRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const [stage, setStage] = useState(STAGES.INTRO);
    const [sessionData, setSessionData] = useState(null);
    const [loadingSession, setLoadingSession] = useState(true);
    const [sessionError, setSessionError] = useState(null);

    // Document state
    const [idImage, setIdImage] = useState(null);
    const [idPreview, setIdPreview] = useState(null);
    const [idProperties, setIdProperties] = useState({});
    const [idScanned, setIdScanned] = useState(false);
    const [processingId, setProcessingId] = useState(false);

    // Client-side guardrail validation state
    const [guardrailResults, setGuardrailResults] = useState(null);
    const [validatingGuardrails, setValidatingGuardrails] = useState(false);

    // Live Camera capture state
    const [isLiveCamera, setIsLiveCamera] = useState(false);
    const [cameraLoading, setCameraLoading] = useState(false);
    const [facingMode, setFacingMode] = useState('environment');

    // Liveness state
    const [startingLiveness, setStartingLiveness] = useState(false);
    const [livenessSessionId, setLivenessSessionId] = useState(null);
    const [finalizing, setFinalizing] = useState(false);
    const [verificationResult, setVerificationResult] = useState(null);
    const [generalError, setGeneralError] = useState(null);
    const [countdown, setCountdown] = useState(3);

    const redirectUrl = sessionData?.returnUrl ||
        sessionData?.redirectUrl ||
        searchParams.get('returnUrl') ||
        searchParams.get('redirectUrl');
    const isCloseAction = redirectUrl?.trim().toUpperCase() === 'CLOSE';

    const handleCloseWindow = () => {
        window.close();
        if (!window.closed && !window.opener) {
            window.open('', '_self', '');
            window.close();
        }
    };

    // Auto-redirect or close effect when verification is complete and returnUrl / redirectUrl is provided
    useEffect(() => {
        if (stage === STAGES.COMPLETED && redirectUrl) {
            const timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        if (isCloseAction) {
                            handleCloseWindow();
                        } else {
                            const separator = redirectUrl.includes('?') ? '&' : '?';
                            const sessionId = sessionData?.sessionId;
                            const sessionParam = sessionId ? `sessionId=${encodeURIComponent(sessionId)}&` : '';
                            window.location.href = `${redirectUrl}${separator}${sessionParam}status=VERIFIED`;
                        }
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [stage, sessionData, redirectUrl, isCloseAction]);

    // 1. Validate Token & Fetch Session
    useEffect(() => {
        if (!token) {
            setLoadingSession(false);
            setSessionError("No verification token provided. Please use the signed link sent to you.");
            return;
        }

        const fetchSession = async () => {
            try {
                const response = await api.get(`verify/session?token=${encodeURIComponent(token)}`);
                let parsedResponse = response;
                if (response && response.body) {
                    parsedResponse = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
                }
                setSessionData(parsedResponse);
                if (parsedResponse.status === 'VERIFIED') {
                    setStage(STAGES.COMPLETED);
                    setVerificationResult(parsedResponse);
                } else if (parsedResponse.status === 'LOCKED' || parsedResponse.status === 'FAILED' || parsedResponse.attemptsRemaining <= 0) {
                    setStage(STAGES.LOCKED);
                    setSessionError(parsedResponse.error || "This verification link has expired or elapsed. Please try again.");
                } else if (parsedResponse.status === 'ID_ANALYZED') {
                    if (parsedResponse.extractedProperties && Object.keys(parsedResponse.extractedProperties).length > 0) {
                        setIdProperties(parsedResponse.extractedProperties);
                    }
                    setIdScanned(true);
                    setStage(STAGES.SELFIE_PREP);
                }
                setLoadingSession(false);
            } catch (err) {
                console.error("Session load error:", err);
                const errData = err.response?.data || {};
                const errMsg = errData.error || errData.message || err.message || "Invalid or expired verification session.";
                if (err.status === 403 || errData.status === 'LOCKED' || errData.status === 'FAILED') {
                    setStage(STAGES.LOCKED);
                    setSessionError(errMsg);
                    if (errData.returnUrl) {
                        setSessionData(prev => ({ ...(prev || {}), returnUrl: errData.returnUrl }));
                    }
                } else {
                    setSessionError(errMsg);
                }
                setLoadingSession(false);
            }
        };

        fetchSession();
    }, [token]);

    // Clean up camera stream on unmount
    useEffect(() => {
        const videoEl = videoRef.current;
        return () => {
            if (videoEl && videoEl.srcObject) {
                videoEl.srcObject.getTracks().forEach(t => t.stop());
            }
        };
    }, []);

    // Live Camera Controls
    const startCamera = async (mode = facingMode) => {
        setIsLiveCamera(true);
        setCameraLoading(true);
        setGeneralError(null);

        // Stop any active stream first
        if (videoRef.current && videoRef.current.srcObject) {
            videoRef.current.srcObject.getTracks().forEach(t => t.stop());
            videoRef.current.srcObject = null;
        }

        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error("Live camera is not supported in this browser. Please use 'Upload Photo from Device'.");
            }

            let stream;
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: { ideal: mode },
                        width: { ideal: 1920 },
                        height: { ideal: 1080 }
                    }
                });
            } catch (err) {
                stream = await navigator.mediaDevices.getUserMedia({ video: true });
            }

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
        } catch (err) {
            console.error("Camera access error:", err);
            setIsLiveCamera(false);
            setGeneralError(err.name === 'NotAllowedError'
                ? "Camera access was denied. Please allow camera permissions in your browser or select 'Upload Photo from Device'."
                : (err.message || "Could not access camera. Please use 'Upload Photo from Device'."));
        } finally {
            setCameraLoading(false);
        }
    };

    const stopCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            videoRef.current.srcObject.getTracks().forEach(t => t.stop());
            videoRef.current.srcObject = null;
        }
        setIsLiveCamera(false);
    };

    const runGuardrails = async (imageSrc) => {
        setValidatingGuardrails(true);
        setGuardrailResults(null);
        setGeneralError(null);
        try {
            const results = await validateIdCardImage(imageSrc);
            setGuardrailResults(results);
            if (!results.passed && results.errors?.length > 0) {
                setGeneralError(`Image Quality Check Failed: ${results.errors[0]}`);
            }
        } catch (err) {
            console.error("Guardrail execution error:", err);
        } finally {
            setValidatingGuardrails(false);
        }
    };

    const capturePhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;

        const w = video.videoWidth || 1280;
        const h = video.videoHeight || 720;
        canvas.width = w;
        canvas.height = h;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, w, h);

        const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

        stopCamera();
        setIdPreview(dataUrl);
        setIdImage(dataUrl);
        setIdScanned(false);
        setIdProperties({});
        setGeneralError(null);
        runGuardrails(dataUrl);
    };

    const flipCamera = () => {
        const newMode = facingMode === 'environment' ? 'user' : 'environment';
        setFacingMode(newMode);
        startCamera(newMode);
    };

    // Handle Document Upload
    const handleDocumentUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        stopCamera();
        const objUrl = URL.createObjectURL(file);
        setIdPreview(objUrl);
        setIdScanned(false);
        setIdProperties({});
        setGeneralError(null);

        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = reader.result;
            setIdImage(base64);
            runGuardrails(base64);
        };
        reader.readAsDataURL(file);
    };

    const handleConfirmId = async () => {
        if (!idImage) return;

        // Architectural Guardrail: Enforce client-side pre-validation passes before posting to AWS
        if (guardrailResults && !guardrailResults.passed) {
            setGeneralError(guardrailResults.errors?.[0] || "Image quality guardrails failed. Please fix issues or retake photo.");
            return;
        }

        setProcessingId(true);
        setGeneralError(null);

        try {
            const payload = {
                sessionId: sessionData?.sessionId,
                image: idImage,
                ImageName: 'id-card.jpg'
            };
            const data = await api.post('extract-idcard', payload);
            let parsedData = data;
            if (data && data.body) {
                parsedData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
            }

            if (parsedData?.error) {
                const errMsg = parsedData.message || parsedData.error;
                setSessionError(`ID document verification failed: ${errMsg}`);
                setGeneralError(errMsg);
                setSessionData(prev => ({ ...prev, attemptsRemaining: 0, status: 'FAILED' }));
                setStage(STAGES.LOCKED);
            } else {
                const props = parsedData?.Properties || {};
                setIdProperties(props);
                setIdScanned(true);
            }
        } catch (err) {
            console.error("ID processing error:", err);
            const errData = err.response?.data || {};
            const errMsg = errData.message || errData.error || err.message || "Failed to process ID document. The document was rejected by the server.";
            setSessionError(`ID document verification failed: ${errMsg}`);
            setGeneralError(errMsg);
            setSessionData(prev => ({ ...prev, attemptsRemaining: 0, status: 'FAILED' }));
            setStage(STAGES.LOCKED);
        } finally {
            setProcessingId(false);
        }
    };

    // Initiate Liveness Session
    const handleStartLiveness = async () => {
        setStartingLiveness(true);
        setGeneralError(null);
        try {
            const sessionIdParam = sessionData?.sessionId ? `?sessionId=${encodeURIComponent(sessionData.sessionId)}` : '';
            const data = await api.get(`start-liveness-session${sessionIdParam}`);
            let parsedData = data;
            if (data && data.body) {
                parsedData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
            }
            const lsid = parsedData?.SessionId || parsedData?.sessionId;
            if (!lsid) {
                throw new Error("Could not initialize face liveness session with the server.");
            }
            setLivenessSessionId(lsid);
            setStage(STAGES.LIVENESS_STREAM);
        } catch (err) {
            console.error("Error starting liveness:", err);
            setGeneralError(err.message || "Could not initialize face liveness session. Please try again.");
        } finally {
            setStartingLiveness(false);
        }
    };

    // Helper to translate backend technical errors into friendly UI messages while logging raw errors to console
    const getFriendlyVerificationErrorMessage = (err, backendData) => {
        const errorCode = backendData?.error || backendData?.code || '';
        const rawMsg = (backendData?.message || err?.message || '').toLowerCase();

        if (errorCode === 'LIVENESS_FAILED' || rawMsg.includes('confidence') || rawMsg.includes('threshold') || rawMsg.includes('liveness')) {
            return "Live presence check failed. For security reasons, this single-use verification link has expired. Please request a new link.";
        }

        if (errorCode === 'FACE_MISMATCH' || rawMsg.includes('match') || rawMsg.includes('mismatch')) {
            return "Your selfie did not match the photo on your ID document. This single-use verification link has expired. Please request a new link.";
        }

        if (errorCode === 'SESSION_LOCKED' || (backendData?.attemptsRemaining !== undefined && backendData.attemptsRemaining <= 0)) {
            return "This single-use verification link has expired. Please request a new verification link to try again.";
        }

        if (errorCode === 'SESSION_NOT_FOUND' || rawMsg.includes('not found') || rawMsg.includes('expired')) {
            return "Your verification session has expired or is invalid. Please request a new verification link.";
        }

        if (rawMsg.includes('id document') || rawMsg.includes('upload your id')) {
            return "ID document photo could not be located. This verification session has expired. Please request a new link.";
        }

        if (rawMsg.includes('permission') || rawMsg.includes('notallowed') || rawMsg.includes('camera')) {
            return "Camera access was interrupted or denied. For security reasons, this single-use link has expired. Please request a new link.";
        }

        return "Face verification failed. This single-use verification session has expired. Please request a new link.";
    };

    // Finalize Verification
    const handleAnalysisComplete = async () => {
        setFinalizing(true);
        setGeneralError(null);

        try {
            const payload = {
                sessionId: sessionData?.sessionId,
                livenessSessionId: livenessSessionId
            };
            const result = await api.post('verify/finalize', payload);
            let parsedResult = result;
            if (result && result.body) {
                parsedResult = typeof result.body === 'string' ? JSON.parse(result.body) : result.body;
            }

            if (parsedResult.status === 'VERIFIED') {
                setVerificationResult(parsedResult);
                setStage(STAGES.COMPLETED);
            } else {
                // Log full backend failure details for debugging
                console.error("Verification not approved (backend response):", {
                    status: parsedResult.status,
                    backendData: parsedResult,
                    message: parsedResult.message
                });

                const friendlyMessage = getFriendlyVerificationErrorMessage(null, parsedResult);
                setGeneralError(friendlyMessage);
                setSessionError(friendlyMessage);
                setSessionData(prev => ({ ...prev, attemptsRemaining: 0, status: 'FAILED' }));
                setStage(STAGES.LOCKED);
            }
        } catch (err) {
            let errData = err.response?.data || {};
            if (typeof errData === 'string') {
                try { errData = JSON.parse(errData); } catch (_) {}
            }
            if (errData && errData.body) {
                try { errData = typeof errData.body === 'string' ? JSON.parse(errData.body) : errData.body; } catch (_) {}
            }

            // Always log the exact raw backend error and response to console
            console.error("Liveness verification failed (backend error):", {
                statusCode: err.response?.status,
                backendData: errData,
                rawErrorMessage: errData?.message || err.message,
                originalError: err
            });

            const friendlyMessage = getFriendlyVerificationErrorMessage(err, errData);
            setGeneralError(friendlyMessage);
            setSessionError(friendlyMessage);
            setSessionData(prev => ({ ...prev, attemptsRemaining: 0, status: 'FAILED' }));
            setStage(STAGES.LOCKED);
        } finally {
            setFinalizing(false);
        }
    };

    // Stepper active calculation
    const getStepperState = () => {
        if (stage === STAGES.INTRO || stage === STAGES.UPLOAD_ID) {
            return { step1: 'active', step2: '', step3: '', connector1: false, connector2: false };
        }
        if (stage === STAGES.SELFIE_PREP || stage === STAGES.LIVENESS_STREAM) {
            return { step1: 'completed', step2: 'active', step3: '', connector1: true, connector2: false };
        }
        if (stage === STAGES.COMPLETED) {
            return { step1: 'completed', step2: 'completed', step3: 'active', connector1: true, connector2: true };
        }
        return { step1: '', step2: '', step3: '', connector1: false, connector2: false };
    };

    const stepper = getStepperState();

    if (loadingSession) {
        return (
            <div className="gi-mobile-card" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
                <div className="gi-spinner" style={{ width: '32px', height: '32px', borderTopColor: '#fbbf24' }} />
                <p style={{ marginTop: '16px', color: 'var(--gi-text-muted)', fontSize: '0.95rem' }}>Verifying security session...</p>
            </div>
        );
    }

    if (sessionError && stage !== STAGES.LOCKED) {
        return (
            <div className="gi-mobile-card">
                <div className="gi-app-header">
                    <div className="gi-brand">
                        <img src="/icons/icon-192x192.png" alt="IDENNTITY" className="gi-brand-icon" />
                        <span>IDENNTITY<span className="gi-brand-dot">.</span></span>
                    </div>
                </div>
                <div className="gi-card-content" style={{ justifyContent: 'center' }}>
                    <div className="gi-alert gi-alert-error" style={{ marginBottom: '24px' }}>
                        {sessionError}
                    </div>
                    <p style={{ color: 'var(--gi-text-muted)', fontSize: '0.9rem', marginBottom: '24px' }}>
                        This link may have expired or is invalid. Please request a new verification link from your administrator.
                    </p>
                    <a href="/" className="gi-btn-secondary">
                        Go to Home
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="gi-mobile-card">
            {/* Header with IDENNTITY branding & attempts badge */}
            <div className="gi-app-header">
                <div className="gi-brand">
                    <img src="/icons/icon-192x192.png" alt="IDENNTITY" className="gi-brand-icon" />
                    <span>IDENNTITY<span className="gi-brand-dot">.</span></span>
                </div>
                {stage !== STAGES.COMPLETED && stage !== STAGES.LOCKED && (
                    <div className="gi-badge-attempts" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#d97706', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                        Single-Use Link
                    </div>
                )}
            </div>

            {/* Stepper matching mockups */}
            <div className="gi-stepper">
                <div className={`gi-step-item ${stepper.step1}`}>
                    <div className="gi-step-dot" />
                    <span className="gi-step-label">Document</span>
                </div>
                <div className={`gi-step-connector ${stepper.connector1 ? 'filled' : ''}`} />
                <div className={`gi-step-item ${stepper.step2}`}>
                    <div className="gi-step-dot" />
                    <span className="gi-step-label">Selfie</span>
                </div>
                <div className={`gi-step-connector ${stepper.connector2 ? 'filled' : ''}`} />
                <div className={`gi-step-item ${stepper.step3}`}>
                    <div className="gi-step-dot" />
                    <span className="gi-step-label">Complete</span>
                </div>
            </div>

            {/* Hidden native file input for uploading existing photo from device/gallery */}
            <input
                ref={fileUploadInputRef}
                type="file"
                accept="image/*"
                onChange={handleDocumentUpload}
                style={{ display: 'none' }}
            />

            {/* Hidden canvas for frame capture */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Global Error Banner */}
            {generalError && (
                <div style={{ padding: '0 28px' }}>
                    <div className="gi-alert gi-alert-error">
                        <span>{generalError}</span>
                    </div>
                </div>
            )}

            {/* SCREEN 1: INTRO */}
            {stage === STAGES.INTRO && (
                <div className="gi-card-content">
                    <div className="gi-illustration-container">
                        <PassportIllustration />
                    </div>

                    <h1 className="gi-title">First, let's get to know you</h1>
                    <p className="gi-subtitle">
                        Please prepare one of the following documents:<br />
                        <strong>Passport &bull; National ID</strong>
                    </p>

                    <div className="gi-action-area">
                        <button
                            className="gi-btn-primary"
                            onClick={() => setStage(STAGES.UPLOAD_ID)}
                        >
                            Let's Go &rarr;
                        </button>

                        <div className="gi-footer-estimate">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                            </svg>
                            <span>Most people finish this step under 3 minutes</span>
                        </div>
                    </div>
                </div>
            )}

            {/* SCREEN 2: UPLOAD ID PHOTO */}
            {stage === STAGES.UPLOAD_ID && (
                <>
                    {/* Live Camera Viewfinder Mode */}
                    {isLiveCamera ? (
                        <div className="gi-card-content" style={{ padding: '12px 18px 20px' }}>
                            <h2 className="gi-title" style={{ fontSize: '1.25rem', marginBottom: '6px' }}>
                                Position your ID
                            </h2>
                            <p className="gi-subtitle" style={{ fontSize: '0.85rem', marginBottom: '14px' }}>
                                Align your ID card or passport inside the box and keep steady.
                            </p>

                            <div className="gi-camera-container">
                                <video
                                    ref={videoRef}
                                    playsInline
                                    muted
                                    className="gi-camera-video"
                                />
                                <div className="gi-camera-overlay">
                                    <div className="gi-camera-card-frame" />
                                    <div className="gi-camera-instruction">
                                        Make sure details are glare-free
                                    </div>
                                </div>
                                {cameraLoading && (
                                    <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: '#FFF' }}>
                                        <div className="gi-spinner" style={{ width: '30px', height: '30px', borderTopColor: '#fbbf24' }} />
                                        <span style={{ fontSize: '0.85rem' }}>Accessing camera...</span>
                                    </div>
                                )}
                            </div>

                            {/* Camera Shutter & Controls */}
                            <div className="gi-camera-shutter-bar">
                                <button
                                    type="button"
                                    className="gi-btn-text"
                                    onClick={stopCamera}
                                    style={{ color: 'var(--gi-text-muted)', fontSize: '0.85rem' }}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    className="gi-camera-shutter-btn"
                                    onClick={capturePhoto}
                                    disabled={cameraLoading}
                                    title="Take Photo"
                                >
                                    <div className="gi-camera-shutter-inner" />
                                </button>

                                <button
                                    type="button"
                                    className="gi-btn-text"
                                    onClick={flipCamera}
                                    title="Flip camera"
                                    style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem' }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
                                    </svg>
                                    Flip
                                </button>
                            </div>
                        </div>
                    ) : !idPreview ? (
                        <div className="gi-card-content">
                            <div className="gi-illustration-container">
                                <IdCardIllustration />
                            </div>

                            <h1 className="gi-title">Upload your ID</h1>
                            <p className="gi-subtitle">
                                Take a live photo with your camera or select an existing photo from your device.
                            </p>

                            <div className="gi-action-area">
                                <button
                                    type="button"
                                    className="gi-btn-primary"
                                    onClick={() => startCamera('environment')}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                        <circle cx="12" cy="13" r="4"/>
                                    </svg>
                                    Take Photo
                                </button>
                                <button
                                    type="button"
                                    className="gi-btn-secondary"
                                    onClick={() => fileUploadInputRef.current?.click()}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                        <polyline points="17 8 12 3 7 8"/>
                                        <line x1="12" y1="3" x2="12" y2="15"/>
                                    </svg>
                                    Upload Photo from Device
                                </button>
                                <button
                                    type="button"
                                    className="gi-btn-text"
                                    onClick={() => setStage(STAGES.INTRO)}
                                >
                                    &larr; Back to instructions
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="gi-card-content">
                            <div className="gi-preview-box">
                                <img src={idPreview} alt="ID Document Preview" />
                            </div>

                            {!idScanned ? (
                                <>
                                    <h2 className="gi-title" style={{ fontSize: '1.3rem' }}>Review your photo</h2>
                                    <p className="gi-subtitle" style={{ marginBottom: '12px' }}>
                                        Make sure all information and your face are completely clear and legible.
                                    </p>

                                    {/* Client-Side Guardrail Quality Diagnostics Panel */}
                                    {validatingGuardrails ? (
                                        <div className="gi-guardrail-panel" style={{ textAlign: 'center', padding: '16px' }}>
                                            <div className="gi-spinner" style={{ width: '22px', height: '22px', margin: '0 auto 8px', borderTopColor: '#fbbf24' }} />
                                            <span style={{ fontSize: '0.82rem', color: 'var(--gi-text-muted)' }}>
                                                Evaluating image quality on device (blur, glare, resolution & face)...
                                            </span>
                                        </div>
                                    ) : (
                                        <></>
                                    )}

                                    <div className="gi-action-area">
                                        <button
                                            type="button"
                                            className="gi-btn-primary"
                                            onClick={handleConfirmId}
                                            disabled={processingId || validatingGuardrails || (guardrailResults && !guardrailResults.passed)}
                                        >
                                            {processingId ? (
                                                <>
                                                    <div className="gi-spinner" />
                                                    <span>Analyzing ID Document...</span>
                                                </>
                                            ) : validatingGuardrails ? (
                                                "Evaluating Quality on Device..."
                                            ) : guardrailResults && !guardrailResults.passed ? (
                                                "Fix Quality Issues to Continue"
                                            ) : (
                                                "Scan & Continue"
                                            )}
                                        </button>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                type="button"
                                                className="gi-btn-secondary"
                                                style={{ flex: 1, padding: '10px 8px', fontSize: '0.85rem' }}
                                                onClick={() => {
                                                    setIdPreview(null);
                                                    setIdImage(null);
                                                    setIdScanned(false);
                                                    setGuardrailResults(null);
                                                    startCamera('environment');
                                                }}
                                                disabled={processingId}
                                            >
                                                Retake Photo
                                            </button>
                                            <button
                                                type="button"
                                                className="gi-btn-secondary"
                                                style={{ flex: 1, padding: '10px 8px', fontSize: '0.85rem' }}
                                                onClick={() => {
                                                    setIdPreview(null);
                                                    setIdImage(null);
                                                    setIdScanned(false);
                                                    setGuardrailResults(null);
                                                    fileUploadInputRef.current?.click();
                                                }}
                                                disabled={processingId}
                                            >
                                                Upload Another
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="gi-alert gi-alert-success">
                                        ✓ Valid face and document detected successfully.
                                    </div>

                                    {Object.keys(idProperties).length > 0 && (
                                        <div className="gi-details-card">
                                            {Object.entries(idProperties).slice(0, 4).map(([k, v]) => v && (
                                                <div className="gi-details-row" key={k}>
                                                    <span className="gi-details-key">{k}</span>
                                                    <span className="gi-details-val">{v}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="gi-action-area">
                                        <button
                                            className="gi-btn-primary"
                                            onClick={() => setStage(STAGES.SELFIE_PREP)}
                                        >
                                            Proceed to Selfie Check &rarr;
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* SCREEN 3: TAKE A SELFIE (PREPARATION) */}
            {stage === STAGES.SELFIE_PREP && (
                <div className="gi-card-content">
                    <div className="gi-illustration-container">
                        <SelfieIllustration />
                    </div>

                    <h1 className="gi-title">Take a selfie</h1>
                    <p className="gi-subtitle">
                        The image should be clear and have your face fully inside the frame.
                    </p>

                    <div className="gi-action-area">
                        <button
                            className="gi-btn-primary"
                            onClick={handleStartLiveness}
                            disabled={startingLiveness}
                        >
                            {startingLiveness ? (
                                <>
                                    <div className="gi-spinner" />
                                    <span>Preparing Camera...</span>
                                </>
                            ) : (
                                "Take a Selfie"
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* SCREEN 4: CAMERA FACE LIVENESS STREAM */}
            {stage === STAGES.LIVENESS_STREAM && (
                <div className="gi-card-content" style={{ padding: '8px 16px 20px' }}>
                    {finalizing ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '60px 20px',
                            gap: '16px'
                        }}>
                            <div className="gi-spinner" style={{ width: '36px', height: '36px', borderTopColor: '#fbbf24' }} />
                            <h2 className="gi-title" style={{ fontSize: '1.25rem' }}>Verifying face match...</h2>
                            <p className="gi-subtitle">Matching your live face against your ID photo...</p>
                        </div>
                    ) : (
                        <Liveness
                            sessionId={livenessSessionId}
                            credentials={sessionData?.credentials}
                            onAnalysisComplete={handleAnalysisComplete}
                            onUserCancel={() => {
                                const cancelMsg = "Face verification was cancelled. For security reasons, this single-use link has expired.";
                                setSessionError(cancelMsg);
                                setGeneralError(cancelMsg);
                                setSessionData(prev => ({ ...prev, attemptsRemaining: 0, status: 'LOCKED' }));
                                setStage(STAGES.LOCKED);
                            }}
                            onError={(err) => {
                                console.error("Liveness streaming error (Amplify client):", err);
                                const errMsg = "Camera face verification was interrupted. For security reasons, this single-use link has expired.";
                                setSessionError(errMsg);
                                setGeneralError(errMsg);
                                setSessionData(prev => ({ ...prev, attemptsRemaining: 0, status: 'LOCKED' }));
                                setStage(STAGES.LOCKED);
                            }}
                        />
                    )}
                </div>
            )}

            {/* SCREEN 5: COMPLETED / VERIFIED */}
            {stage === STAGES.COMPLETED && (
                <div className="gi-card-content">
                    <div className="gi-success-check">
                        ✓
                    </div>

                    <h1 className="gi-title">Verification Successful!</h1>
                    <p className="gi-subtitle">
                        Your identity has been authenticated and matched against your official documentation.
                    </p>

                    {verificationResult?.similarity && (
                        <div style={{
                            display: 'inline-block',
                            padding: '6px 14px',
                            backgroundColor: 'var(--gi-alert-success-bg)',
                            border: '1px solid var(--gi-alert-success-border)',
                            borderRadius: '20px',
                            color: 'var(--gi-alert-success-text)',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            margin: '10px auto'
                        }}>
                            Face Match Score: {parseFloat(verificationResult.similarity).toFixed(1)}%
                        </div>
                    )}

                    {redirectUrl ? (
                        <>
                            <p className="gi-subtitle" style={{ color: 'var(--gi-accent-text)', fontWeight: 600, marginTop: '8px' }}>
                                {isCloseAction
                                    ? (countdown > 0
                                        ? `Closing this window in ${countdown}s...`
                                        : 'You can now close this window.')
                                    : `Redirecting you back to your application in ${countdown}s...`}
                            </p>
                            <div className="gi-action-area" style={{ marginTop: '24px' }}>
                                <button
                                    className="gi-btn-primary"
                                    onClick={() => {
                                        if (isCloseAction) {
                                            handleCloseWindow();
                                        } else {
                                            const separator = redirectUrl.includes('?') ? '&' : '?';
                                            const sessionId = sessionData?.sessionId;
                                            const sessionParam = sessionId ? `sessionId=${encodeURIComponent(sessionId)}&` : '';
                                            window.location.href = `${redirectUrl}${separator}${sessionParam}status=VERIFIED`;
                                        }
                                    }}
                                >
                                    {isCloseAction ? 'Close Window' : <>Continue Now &rarr;</>}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="gi-subtitle">
                                You can now return to the application or portal that requested this verification.
                            </p>
                            <div className="gi-action-area" style={{ marginTop: '24px' }}>
                                <button
                                    className="gi-btn-primary"
                                    onClick={() => {
                                        if (window.opener) {
                                            window.close();
                                        } else {
                                            window.location.href = '/';
                                        }
                                    }}
                                >
                                    Done
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* SCREEN 6: LOCKED / EXPIRED SINGLE-USE */}
            {stage === STAGES.LOCKED && (
                <div className="gi-card-content" style={{ justifyContent: 'center' }}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: 'var(--gi-alert-error-bg)',
                        color: 'var(--gi-alert-error-text)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '28px',
                        margin: '0 auto 18px'
                    }}>
                        ✕
                    </div>
                    <h1 className="gi-title" style={{ color: 'var(--gi-alert-error-text)' }}>Verification Expired</h1>
                    <p className="gi-subtitle">
                        {sessionError || generalError || "This verification session has expired or has been locked for security."}
                    </p>
                    <p style={{ fontSize: '0.82rem', color: 'var(--gi-text-muted)', margin: '8px 0 20px', textAlign: 'center' }}>
                        Each verification link allows one document check and one selfie check. Please generate or request a new link to proceed.
                    </p>
                    <div className="gi-action-area">
                        {redirectUrl ? (
                            <button
                                className="gi-btn-secondary"
                                onClick={() => {
                                    if (isCloseAction) {
                                        handleCloseWindow();
                                    } else {
                                        const separator = redirectUrl.includes('?') ? '&' : '?';
                                        const sessionId = sessionData?.sessionId;
                                        const sessionParam = sessionId ? `sessionId=${encodeURIComponent(sessionId)}&` : '';
                                        window.location.href = `${redirectUrl}${separator}${sessionParam}status=LOCKED`;
                                    }
                                }}
                            >
                                {isCloseAction ? 'Close Window' : <>Return to Application &rarr;</>}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="gi-btn-secondary"
                                onClick={() => {
                                    if (window.opener) {
                                        window.close();
                                    } else {
                                        window.location.href = '/';
                                    }
                                }}
                            >
                                Close & Return
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default VerificationWizard;
